import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * The keyboard shortcuts, in one place.
 *
 * Every one of these is also in the tooltip of the control it drives, but a tooltip only
 * answers a question the admin already knew to ask. This is what makes the set
 * discoverable — and the editor is a tool people use for an hour at a time, where the
 * difference between clicking and typing adds up.
 */
const SHORTCUTS: ReadonlyArray<{ readonly keys: string; readonly labelKey: string }> = [
  { keys: 'V', labelKey: 'floorplan-editor-tool-select' },
  { keys: 'E', labelKey: 'floorplan-editor-tool-vertex' },
  { keys: 'D', labelKey: 'floorplan-editor-tool-desk' },
  { keys: 'B', labelKey: 'floorplan-editor-tool-desk-group' },
  { keys: 'S', labelKey: 'floorplan-editor-tool-desk-shape' },
  { keys: 'T', labelKey: 'floorplan-editor-tool-text' },
  { keys: 'I', labelKey: 'floorplan-editor-tool-icon' },
  { keys: 'P', labelKey: 'floorplan-editor-tool-polygon' },
  { keys: 'R', labelKey: 'floorplan-editor-tool-rectangle' },
  { keys: 'C', labelKey: 'floorplan-editor-tool-ellipse' },
  { keys: 'G', labelKey: 'floorplan-editor-grid-lines' },
  { keys: 'Enter', labelKey: 'floorplan-editor-shortcut-close-shape' },
  { keys: '⇧ / Ctrl / Alt', labelKey: 'floorplan-editor-shortcut-straighten' },
  { keys: 'Ctrl/⌘ + Z', labelKey: 'floorplan-editor-undo' },
  { keys: 'Ctrl/⌘ + ⇧ + Z', labelKey: 'floorplan-editor-redo' },
  { keys: 'Ctrl/⌘ + S', labelKey: 'save' },
  { keys: 'Ctrl/⌘ + A', labelKey: 'floorplan-editor-shortcut-select-all' },
  { keys: 'Ctrl/⌘ + K', labelKey: 'floorplan-editor-shortcut-commands' },
  { keys: 'Ctrl/⌘ + F', labelKey: 'floorplan-editor-shortcut-find' },
  { keys: 'Ctrl/⌘ + D', labelKey: 'floorplan-editor-shortcut-duplicate' },
  { keys: '⇧ + click', labelKey: 'floorplan-editor-shortcut-add-to-selection' },
  { keys: 'Ctrl/⌘ + drag', labelKey: 'floorplan-editor-shortcut-box-select' },
  { keys: '← ↑ → ↓', labelKey: 'floorplan-editor-shortcut-nudge' },
  { keys: '⇧ + ← ↑ → ↓', labelKey: 'floorplan-editor-shortcut-nudge-far' },
  { keys: 'Alt + drag', labelKey: 'floorplan-editor-shortcut-straighten-drag' },
  { keys: 'Alt + click', labelKey: 'floorplan-editor-shortcut-remove-point' },
  { keys: 'Delete', labelKey: 'floorplan-editor-shortcut-delete' },
  { keys: 'Esc', labelKey: 'floorplan-editor-shortcut-cancel' },
  { keys: 'Scroll', labelKey: 'floorplan-editor-shortcut-zoom' },
  { keys: 'Middle-drag', labelKey: 'floorplan-editor-shortcut-pan' },
  { keys: 'Right-click', labelKey: 'floorplan-editor-context-menu' },
];

const ShortcutsDialog = ({ open, onClose }: Props): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('floorplan-editor-shortcuts')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {SHORTCUTS.map(({ keys, labelKey }) => (
            <Stack key={keys} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="body2">{t(labelKey)}</Typography>
              <Box
                component="kbd"
                sx={{
                  border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
                  borderRadius: 1,
                  px: 1,
                  py: 0.25,
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  color: StyleConstants.GREY_COLOR,
                }}
              >
                {keys}
              </Box>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ShortcutsDialog;
